<template>
	<UApp :toaster="{ position: 'top-right' }">
		<!-- Debug panel for development -->
		<ClientOnly>
			<LazyGA4DebugPanel
				v-if="showDebug"
				@close="showDebug = false"
			/>
		</ClientOnly>

		<ClientOnly>
			<LazyBannersAlert
				v-if="showAlertBanner && !$device.isCrawler && route.meta.layout !== ('blank' as RouteMeta['LayoutKey'])"
				:id="alert.id"
				:title="alert.title"
				:message="alert.message"
				:active="alert.active"
				:updated="alert.updated"
				:created-time="alert.createdTime"
				always-show-on-load
				@close="alert.open = false"
			/>
		</ClientOnly>

		<AppHeader v-if="route.meta.layout !== ('blank' as RouteMeta['LayoutKey'])" />

		<UMain :class="showAlertBanner ? 'min-h-page-alert' : 'min-h-page-header'">
			<NuxtLayout :show-alert-banner="showAlertBanner">
				<NuxtPage />
			</NuxtLayout>
		</UMain>
	</UApp>
</template>

<script setup lang="ts">
import MyWorker from '@/assets/workers/alertWorker?worker'
import { useAuth } from '@/composables/useAuth'
import { useAlert } from '@/composables/useState'
import { isAlertResponseValid } from '@/helpers/api-response-validators'
import { useI18n } from 'vue-i18n'
import type { RouteMeta } from 'vue-router'
import { useRoute } from 'vue-router'

import { IS_DEVELOPMENT } from '@/constants/index.js'

const { locale } = useI18n({
	useScope: 'global'
})

const { $intercom } = useNuxtApp()
const { intercomSettings } = useAuth()

useHead({
	meta: [
		{
			name: 'viewport',
			content: 'width=device-width, initial-scale=1'
		}
	],
	link: [
		{
			rel: 'icon',
			href: '/favicons/favicon.ico'
		}
	],
	htmlAttrs: {
		lang: () => {
			return locale.value
		}
	}
})

const route = useRoute()

const alert = useAlert()
const worker = ref<Worker | null>(null)

const showAlertBanner = computed(() => {
	return alert.value.active // (alert.value.active && alert.value.open)
})

const checkForAlerts = () => {
	worker.value = new MyWorker()
	worker.value.onmessage = res => {
		if (!res.data?.length) {
			alert.value.id = ''
			alert.value.title = ''
			alert.value.message = ''
			alert.value.createdTime = ''
			alert.value.updated = ''
			alert.value.active = false
			alert.value.open = false
			alert.value.expanded = false
			return
		}
		if (res?.data?.isError) {
			reportError(res?.data?.error)
			return
		}
		if (isAlertResponseValid(res?.data?.[0])) {
			reportError('Invalid data returned from alerts endpoint')
			return
		}
		const {
			id, title, message, active, createdTime, updated
		} = res.data[0]

		alert.value.id = id.toString() || ''
		alert.value.createdTime = createdTime || ''
		alert.value.updated = updated || ''
		alert.value.title = title || ''
		alert.value.message = message || ''
		alert.value.active = active ?? false
		alert.value.open = true
		alert.value.expanded = false
	}
}

onMounted(() => {
	checkForAlerts()
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible' && !worker.value) {
			checkForAlerts()
		} else {
			worker.value?.terminate()
			worker.value = null
		}
	})
	$intercom?.boot(intercomSettings.value)
})

watch(route, newVal => {
	if ((newVal.name as string)?.startsWith('contact')) {
		setTimeout(() => {
			$intercom?.update({
				hide_default_launcher: false
			})
		}, 1000)
	} else {
		$intercom?.update({
			hide_default_launcher: true
		})
	}
}, { immediate: true })

const title = 'Maverix Broadband - Fiber-fast internet, no start-up fees'
const description = 'Maverix Broadband is a fiber internet provider that offers fast, reliable internet with no start-up fees. Experience the difference with Maverix today!'

useSeoMeta({
	title,
	description,
	ogUrl: 'https://maverixbroadband.com',
	ogTitle: title,
	ogDescription: description,
	ogImage: '/images/og-image-fallback.png', // fallback for any other pages that don't have specific OG images
	ogLocale: 'en_US',
	ogLocaleAlternate: [ 'es_MX', 'en_US' ],
	colorScheme: 'only light',
	twitterCard: 'summary_large_image',
	twitterTitle: title,
	twitterDescription: description,
	twitterImage: '/images/og-image-fallback.png'
	// fbAppId: '1234567890', // TODO: Add Facebook App ID so that FB Insights tracking works
})

const showDebug = ref(false)

// Development keyboard shortcut
if (IS_DEVELOPMENT) {
	onMounted(() => {
		const handleKeypress = (e: KeyboardEvent) => {
			if (e.ctrlKey && e.shiftKey && e.key === 'G') {
				showDebug.value = !showDebug.value
			}
		}
		window.addEventListener('keydown', handleKeypress)
		onUnmounted(() => window.removeEventListener('keydown', handleKeypress))
	})
}
</script>
